import { useState, useMemo, useRef, useCallback, useEffect } from 'react'
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { AgGridReact } from 'ag-grid-react'
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import '../../shared/ui/ag-grid-theme.css'
import type {
  ColDef,
  ColumnResizedEvent,
  DragStoppedEvent,
  FilterChangedEvent,
  GridApi,
  GridReadyEvent,
  RowDataUpdatedEvent,
  SortChangedEvent,
  ValueFormatterParams,
  ViewportChangedEvent,
} from 'ag-grid-community'
import { OrderStatus, ORDER_STATUS_LABELS_RU } from '@crm/shared'
import type { OrderStatus as OrderStatusT, OrderRow } from '@crm/shared'
import { getOrders, deleteOrder } from '../../shared/api/orders.api'
import { confirm } from '../../shared/ui/confirmStore'
import { toast } from '../../shared/ui/toastStore'
import { PageLoader } from '../../shared/ui/Spinner'
import { EmptyState } from '../../shared/ui/EmptyState'
import { ErrorMessage } from '../../shared/ui/ErrorMessage'
import { OrderStatusBadge } from './components/OrderStatusBadge'
import { OrderFunnel } from './components/OrderFunnel'
import { CheckboxSetFilter } from '../table/CheckboxSetFilter'
import { AG_GRID_LOCALE_RU } from '../../shared/ag-grid/locale'
import { useGridSelection } from '../../shared/hooks/useGridSelection'
import { useAuthStore } from '../auth/authStore'
import { useOrderColState } from './hooks/useOrderColState'
import { formatMoney, formatDate } from './orderFormat'
import { UnfInvoiceButton } from './unf-invoice/UnfInvoiceButton'
import { UnfInvoiceReviewsButton } from './unf-invoice/UnfInvoiceReviews'

ModuleRegistry.registerModules([AllCommunityModule])

type OrderGridRow = {
  id: string
  requestNumber: string
  clientName: string
  managerName: string
  status: OrderStatusT
  statusLabel: string
  totalSum: number
  invoicedSum: number
  paidSum: number
  remaining: number
  invoiceCount: number
  createdAt: string
}

const moneyFmt = (p: ValueFormatterParams) =>
  p.value == null ? '' : formatMoney(p.value as number)

export function OrdersPage() {
  const user = useAuthStore((s) => s.user)
  const qc = useQueryClient()
  const canDelete =
    user?.role === 'ADMIN' ||
    user?.role === 'MANAGER' ||
    user?.role === 'ROP' ||
    user?.role === 'ASSISTANT'
  const [status, setStatus] = useState<OrderStatusT | ''>('')
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')

  // Live-поиск как в «Заявках»: фильтрация на лету с debounce 300 мс,
  // без кнопки «Найти» (просьба заказчика 02.09).
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300)
    return () => clearTimeout(t)
  }, [searchInput])

  const deleteMutation = useMutation({
    mutationFn: (orderId: string) => deleteOrder(orderId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['orders'] })
      toast.success('Заказ удалён')
    },
    onError: (e) => {
      const err = e as { response?: { data?: { error?: { message?: string } } } }
      toast.error(err.response?.data?.error?.message ?? 'Не удалось удалить заказ')
    },
  })

  const deleteOrderMutate = deleteMutation.mutate
  const handleDelete = useCallback(
    async (row: OrderGridRow) => {
      const ok = await confirm({
        title: `Удалить заказ по заявке ${row.requestNumber}?`,
        message:
          (row.paidSum > 0
            ? `По заказу зафиксированы оплаты на ${formatMoney(row.paidSum)}. `
            : '') +
          'Счета, платежи, документы и история заказа будут удалены. Заявка и её статусы не изменятся. Действие необратимо.',
        danger: true,
        confirmText: 'Удалить',
      })
      if (!ok) return
      deleteOrderMutate(row.id)
    },
    // mutate стабилен, а объект useMutation новый на каждый рендер: зависимость
    // от него пересоздавала колонки и закрывала открытый фильтр колонки.
    [deleteOrderMutate],
  )

  const query = useInfiniteQuery({
    queryKey: ['orders', { status, search }],
    queryFn: ({ pageParam }) =>
      getOrders({
        ...(status ? { status } : {}),
        ...(search ? { search } : {}),
        cursor: pageParam,
        limit: 1000,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  })

  const items: OrderRow[] = useMemo(
    () => query.data?.pages.flatMap((p) => p.items) ?? [],
    [query.data],
  )

  // Автоподгрузка следующей страницы при прокрутке к концу списка (паритет
  // с «Очередью квот»). Грид со внутренним скроллом, поэтому не сентинел под
  // таблицей, а viewportChanged AG Grid: видна одна из 20 последних строк →
  // тянем дальше.
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query
  const onViewportChanged = useCallback(
    (e: ViewportChangedEvent) => {
      if (!hasNextPage || isFetchingNextPage) return
      const total = e.api.getDisplayedRowCount()
      if (total > 0 && e.lastRow >= total - 20) void fetchNextPage()
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage],
  )

  const rowData = useMemo<OrderGridRow[]>(
    () =>
      items.map((o) => ({
        id: o.id,
        requestNumber: o.requestNumber ?? '—',
        clientName: o.clientName,
        managerName: o.managerName ?? '—',
        status: o.status,
        statusLabel: ORDER_STATUS_LABELS_RU[o.status],
        totalSum: o.totalSum,
        invoicedSum: o.invoicedSum,
        paidSum: o.paidSum,
        remaining: o.remainingSum,
        invoiceCount: o.invoiceCount,
        createdAt: o.createdAt,
      })),
    [items],
  )

  const gridRef = useRef<AgGridReact>(null)
  const { onCellMouseDown, onCellMouseOver, getCellStyle } = useGridSelection(gridRef)
  const { saveState, onGridReady: restoreColState } = useOrderColState(
    'crm_orders_cols_v1',
    user?.id,
  )

  // Воронка считается по тем же заказам, что видны в таблице: фильтры колонок
  // (менеджер, клиент, статус…) работают на стороне сайта, поэтому берём строки
  // после фильтра AG Grid. null — фильтров колонок нет, воронка по всей выборке.
  const [filteredIds, setFilteredIds] = useState<Set<string> | null>(null)
  const syncFilteredIds = useCallback((api: GridApi) => {
    if (api.isDestroyed()) return
    if (!api.isAnyFilterPresent()) {
      setFilteredIds(null)
      return
    }
    const ids = new Set<string>()
    api.forEachNodeAfterFilter((n) => {
      if (n.data) ids.add((n.data as OrderGridRow).id)
    })
    setFilteredIds(ids)
  }, [])
  const onGridReady = useCallback(
    (e: GridReadyEvent) => {
      restoreColState(e)
      syncFilteredIds(e.api)
    },
    [restoreColState, syncFilteredIds],
  )
  const onFilterChanged = useCallback(
    (e: FilterChangedEvent) => syncFilteredIds(e.api),
    [syncFilteredIds],
  )
  const onRowDataUpdated = useCallback(
    (e: RowDataUpdatedEvent) => syncFilteredIds(e.api),
    [syncFilteredIds],
  )
  const funnelOrders = useMemo(
    () => (filteredIds ? items.filter((o) => filteredIds.has(o.id)) : items),
    [items, filteredIds],
  )
  // Таблица скрыта (пустой список) — фильтр колонок не действует.
  useEffect(() => {
    if (items.length === 0) setFilteredIds(null)
  }, [items.length])

  const colDefs = useMemo<ColDef<OrderGridRow>[]>(
    () => [
      {
        field: 'requestNumber',
        headerName: 'Заявка',
        minWidth: 150,
        cellRenderer: ({ data }: { data: OrderGridRow }) => (
          <Link
            to={`/orders/${data.id}`}
            className="text-brand-600 hover:text-brand-700 font-medium"
          >
            {data.requestNumber}
          </Link>
        ),
      },
      {
        field: 'clientName',
        headerName: 'Клиент',
        flex: 2,
        minWidth: 180,
        wrapText: true,
        autoHeight: true,
      },
      {
        field: 'managerName',
        headerName: 'Менеджер',
        minWidth: 150,
        cellClass: 'text-gray-600',
        wrapText: true,
        autoHeight: true,
      },
      {
        field: 'statusLabel',
        headerName: 'Статус',
        minWidth: 150,
        cellRenderer: ({ data }: { data: OrderGridRow }) => (
          <OrderStatusBadge status={data.status} />
        ),
      },
      {
        field: 'totalSum',
        headerName: 'Сумма (КП)',
        minWidth: 120,
        filter: false,
        cellClass: 'text-right text-gray-500',
        headerClass: 'ag-right-aligned-header',
        valueFormatter: moneyFmt,
      },
      {
        field: 'invoicedSum',
        headerName: 'По счетам',
        minWidth: 120,
        filter: false,
        cellClass: 'text-right',
        headerClass: 'ag-right-aligned-header',
        valueFormatter: moneyFmt,
      },
      {
        field: 'paidSum',
        headerName: 'Оплачено',
        minWidth: 120,
        filter: false,
        cellClass: 'text-right text-green-700',
        headerClass: 'ag-right-aligned-header',
        valueFormatter: moneyFmt,
      },
      {
        field: 'remaining',
        headerName: 'Осталось',
        minWidth: 120,
        filter: false,
        cellClass: 'text-right text-gray-700',
        headerClass: 'ag-right-aligned-header',
        valueFormatter: moneyFmt,
      },
      {
        field: 'invoiceCount',
        headerName: 'Счетов',
        minWidth: 90,
        filter: false,
        cellClass: 'text-center',
        headerClass: 'ag-right-aligned-header',
      },
      {
        field: 'createdAt',
        headerName: 'Создан',
        minWidth: 120,
        cellClass: 'text-gray-500',
        valueFormatter: (p: ValueFormatterParams) => formatDate(p.value as string),
      },
      ...(canDelete
        ? [
            {
              colId: 'actions',
              headerName: '',
              width: 90,
              minWidth: 80,
              maxWidth: 110,
              filter: false,
              sortable: false,
              resizable: false,
              suppressMovable: true,
              cellClass: 'text-center',
              cellRenderer: ({ data }: { data: OrderGridRow }) => (
                <button
                  className="text-xs font-medium text-red-600 hover:text-red-700 hover:underline"
                  title="Удалить заказ"
                  onClick={(e) => {
                    e.stopPropagation()
                    void handleDelete(data)
                  }}
                >
                  Удалить
                </button>
              ),
            } as ColDef<OrderGridRow>,
          ]
        : []),
    ],
    [canDelete, handleDelete],
  )

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      resizable: true,
      suppressMovable: false,
      filter: CheckboxSetFilter,
      floatingFilter: false,
      cellStyle: getCellStyle,
    }),
    [getCellStyle],
  )

  const saveCb = useCallback((api: Parameters<typeof saveState>[0]) => saveState(api), [saveState])

  return (
    <div className="space-y-4">
      <div className="flex items-baseline gap-3">
        <h1 className="text-xl font-semibold text-gray-900">Заказы</h1>
        {query.data && <span className="text-sm text-gray-500">загружено: {items.length}</span>}
        <span className="ml-auto flex flex-wrap gap-2">
          <UnfInvoiceReviewsButton />
          <UnfInvoiceButton />
        </span>
      </div>

      <OrderFunnel
        orders={funnelOrders}
        partial={Boolean(query.hasNextPage)}
        filtered={filteredIds !== null}
      />

      <div className="flex flex-wrap items-center gap-2">
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as OrderStatusT | '')}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white"
        >
          <option value="">Все статусы</option>
          {Object.values(OrderStatus).map((s) => (
            <option key={s} value={s}>
              {ORDER_STATUS_LABELS_RU[s]}
            </option>
          ))}
        </select>
        <input
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white flex-1 min-w-[220px] max-w-md"
          placeholder="Поиск: № заявки / клиент / № заказа / партномер"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
      </div>

      {query.isLoading ? (
        <PageLoader label="Загружаю заказы…" />
      ) : query.isError ? (
        // Без этой ветки обрыв сети и 500 выглядели как «Заказов нет».
        <ErrorMessage error={query.error} />
      ) : items.length === 0 ? (
        <EmptyState
          title="Заказов нет"
          description="Заказ появится после выгрузки первого счёта из карточки заявки."
        />
      ) : (
        <>
          <div className="crm-table ag-theme-alpine rounded-xl overflow-hidden border border-gray-200 bg-white shadow-sm h-[calc(100vh-280px)]">
            <AgGridReact
              ref={gridRef}
              rowData={rowData}
              columnDefs={colDefs}
              defaultColDef={defaultColDef}
              localeText={AG_GRID_LOCALE_RU}
              getRowId={(p) => p.data.id}
              onGridReady={onGridReady}
              onCellMouseDown={onCellMouseDown}
              onCellMouseOver={onCellMouseOver}
              onDragStopped={(e: DragStoppedEvent) => saveCb(e.api)}
              onColumnResized={(e: ColumnResizedEvent) => {
                if (e.finished) saveCb(e.api)
              }}
              onSortChanged={(e: SortChangedEvent) => saveCb(e.api)}
              onFilterChanged={onFilterChanged}
              onRowDataUpdated={onRowDataUpdated}
              onViewportChanged={onViewportChanged}
              rowHeight={40}
              headerHeight={40}
              suppressDragLeaveHidesColumns
              suppressCellFocus
            />
          </div>

          {query.isFetchingNextPage && (
            <p className="text-center text-sm text-gray-500">Загружаю ещё…</p>
          )}
        </>
      )}
    </div>
  )
}
